import { useEffect, useState } from "react"
import { getAllStates, getDistrictsByState } from "../../../../services/LocationService";
import { getAllWorkTypes } from "../../../../services/AdminServices";
import { IoClose, IoTrashOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";

const AddStaffPlanEstimateForm = ({ onSubmit, editData, onClose, open }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        nameOfWork: "",
        stateId: "",
        districtId: "",
        workTypeId: "",
        estimateCost: "",
        date: ""
    });

    const [attachmentRows, setAttachmentRows] = useState([
        { id: Date.now(), description: "", file: null, fileName: "" }
    ]);

    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [workTypes, setWorkTypes] = useState([]);

    useEffect(() => {
        getAllStates().then(setStates).catch(console.error);
        fetchWorkType();
    }, []);

    useEffect(() => {
        if (formData.stateId) {
            getDistrictsByState(formData.stateId).then(setDistricts).catch(console.error);
        } else {
            setDistricts([]);
        }
    }, [formData.stateId]);

    const fetchWorkType = async () => {
        try {
            const res = await getAllWorkTypes();
            setWorkTypes(res.data);
        } catch (err) {
            console.log("Failed to fetch worktpes", err);
        }
    }

    useEffect(() => {
        if (editData && open) {
            setFormData({
                nameOfWork: editData.nameOfWork || "",
                stateId: editData.stateId || "",
                districtId: editData.districtId || "",
                workTypeId: editData.workTypeId || "",
                estimateCost: editData.estimateCost || "",
                date: editData.date || ""
            });
            if (editData.attachmentData) {
                setAttachmentRows(editData.attachmentData.map(item => ({
                    id: item.id,
                    description: item.description || "",
                    file: null,
                    fileName: item.attachmentName || ""
                })));
            }
        } else {
            resetForm();
        }
    }, [editData, open]);

    const resetForm = () => {
        setFormData({ nameOfWork: "", stateId: "", districtId: "", workTypeId: "", estimateCost: "", date: "" });
        setAttachmentRows([{ id: Date.now(), description: "", file: null, fileName: "" }]);
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = { ...prev, [name]: value };
            if (name === "stateId") newData.districtId = ""; // Reset District if State changes
            return newData;
        });
    };

    const addRow = () => {
        setAttachmentRows([...attachmentRows, { id: Date.now(), description: "", file: null, fileName: "" }]);
    }

    const removeRow = (id) => {
        if (attachmentRows.length > 1) {
            setAttachmentRows(attachmentRows.filter(row => row.id !== id));
        }
    };

    const handleRowChange = (id, field, value) => {
        setAttachmentRows(prev => prev.map(row => row.id === id ? { ...row, [field]: value } : row));
    }

    const handleFileChange = (id, e) => {
        const file = e.target.files[0];
        if (file) {
            setAttachmentRows(prev => prev.map(row =>
                row.id === id ? { ...row, file: file, fileName: file.name } : row
            ));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const planDto = {
            ...formData,
            districtId: Number(formData.districtId),
            workTypeId: Number(formData.workTypeId),
            estimateCost: Number(formData.estimateCost),
            attachments: attachmentRows.map(row => ({
                id: row.id > 1000000000000 ? null : row.id,
                description: row.description.trim()
            }))
        };

        const finalFormData = new FormData();
        finalFormData.append("plan", new Blob([JSON.stringify(planDto)], { type: "application/json" }));

        attachmentRows.forEach((row) => {
            if (row.file) {
                finalFormData.append("files", row.file);
            } else {
                finalFormData.append("files", new Blob([], { type: "application/octet-stream" }));
            }
        });

        onSubmit(finalFormData);
    };

    if (!open) return null;

    return (
     <div className="staff-modal-overlay">
        <div className="staff-modal" style={{ width: '1000px', maxWidth: '95%' }}>
            <div className="staff-modal-header">
                {/* Page Title */}
                <div className="staff-modal-title">{t('pageTitles.planEstimate')}</div>
                <button className="admin-modal-close" onClick={onClose}><IoClose /></button>
            </div>
            <div className="staff-form-body">
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="form-group">
                            {/* Work Name Label */}
                            <label className="staff-modal-label">{t('forms.planEstimate.workName')} <span>*</span></label>
                            <input name="nameOfWork" placeholder="Work Name" value={formData.nameOfWork} onChange={handleFieldChange} className="staff-modal-input" required />
                        </div>
                        <div className="form-group">
                            {/* State Label */}
                            <label className="staff-modal-label">{t('common.state', 'State')} <span>*</span></label>
                            <select name="stateId" value={formData.stateId} onChange={handleFieldChange} className="staff-modal-input" required>
                                <option value="">Select State</option>
                                {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            {/* Location Label */}
                            <label className="staff-modal-label">{t('forms.planEstimate.location')} <span>*</span></label>
                            <select name="districtId" value={formData.districtId} onChange={handleFieldChange} className="staff-modal-input" required>
                                <option value="">Select Location</option>
                                {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            {/* Type of Work Label */}
                            <label className="staff-modal-label">{t('forms.planEstimate.workType')} <span>*</span></label>
                            <select name="workTypeId" value={formData.workTypeId} onChange={handleFieldChange} className="staff-modal-input" required>
                                <option value="">Select Work Type</option>
                                {workTypes.map(w => <option key={w.id} value={w.id}>{w.workTypeName}</option>)}
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="form-group">
                                {/* Estimate Cost Label */}
                                <label className="staff-modal-label">{t('forms.planEstimate.estimateCost')} <span>*</span></label>
                                <input type="number" placeholder="0.00" name="estimateCost" value={formData.estimateCost} onChange={handleFieldChange} className="staff-modal-input" required />
                            </div>
                            <div className="form-group">
                                {/* Date Label */}
                                <label className="staff-modal-label">{t('forms.planEstimate.date')} <span>*</span></label>
                                <input type="date" name="date" value={formData.date} onChange={handleFieldChange} className="staff-modal-input" required />
                            </div>
                        </div>
                    </div>

                    {/* Add Attachment Button */}
                    <button type="button" onClick={addRow} className="staff-btn-save text-sm py-1 mb-2">
                        {t('common.addAttachment')}
                    </button>

                    <table className="w-full border text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                {/* Table Headers */}
                                <th className="border p-2">{t('common.description')}</th>
                                <th className="border p-2">{t('common.attachments')}</th>
                                <th className="border p-2">{t('common.delete')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {attachmentRows.map(row => (
                                <tr key={row.id}>
                                    <td className="border p-2">
                                        <input value={row.description} onChange={(e) => handleRowChange(row.id, 'description', e.target.value)} className="staff-modal-input w-full" placeholder="Description" required />
                                    </td>
                                    <td className="border p-2">
                                        <input type="file" onChange={(e) => handleFileChange(row.id, e)} className="text-xs" />
                                        {row.fileName && <div className="text-blue-600 text-xs mt-1">{row.fileName}</div>}
                                    </td>
                                    <td className="border p-2 text-center">
                                        <button type="button" onClick={() => removeRow(row.id)} className="text-red-500"><IoTrashOutline size={20} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="flex justify-center mt-6">
                        {/* Save Button */}
                        <button type="submit" className="staff-btn-save px-12">
                            {t('common.save')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
    );
};

export default AddStaffPlanEstimateForm;